// Format currency values for display. PT-BR locale, BRL.
// All amounts are in cents (integer) unless otherwise noted.

const BRL_FORMAT = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
});

const NUM_FORMAT = new Intl.NumberFormat("pt-BR");

const BRL_DIGITS = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Format integer cents as BRL currency string, e.g. 150000 → "R$ 1.500,00". */
export function formatCurrencyCents(cents: number | null | undefined): string {
  const value = typeof cents === "number" && Number.isFinite(cents) ? cents : 0;
  return BRL_FORMAT.format(value / 100);
}

/** Format a number in PT-BR locale, e.g. 1500 → "1.500". */
export function formatNumber(value: number): string {
  return NUM_FORMAT.format(value);
}

/** Format a percentage with one decimal, e.g. 0.1234 → "12,3%". */
export function formatPct(ratio: number): string {
  return `${(ratio * 100).toFixed(1).replace(".", ",")}%`;
}

// ── Signed BRL helpers (decimal units, NOT cents) ─────────────────────────────
// `formatBRLSigned` and `formatBRLAlwaysSigned` differ only in how they
// treat zero. The two sign semantics exist because legacy ports pin them in
// snapshot tests:
//   - Wallet stats cards expect "R$ 0,00" (no sign) for zero profit.
//   - Trade rows / martingale chains expect "+R$ 0,00" for pending entries.

/** Sign decimal as `+R$`/`R$`/`-R$`. e.g. 1.5 → "+R$ 1,50", 0 → "R$ 0,00". */
export function formatBRLSigned(value: number): string {
  if (value > 0) return `+R$ ${BRL_DIGITS.format(value)}`;
  if (value < 0) return `-R$ ${BRL_DIGITS.format(Math.abs(value))}`;
  return "R$ 0,00";
}

/** Always-signed decimal: zero/positive use `+`, negative `-`. */
export function formatBRLAlwaysSigned(value: number): string {
  const sign = value < 0 ? "-" : "+";
  return `${sign}R$ ${BRL_DIGITS.format(Math.abs(value))}`;
}

/** Parse a decimal-string balance (e.g. "1500.00") and format as BRL. */
export function formatBRLFromDecimalString(decimal: string): string {
  const num = parseFloat(decimal);
  if (!Number.isFinite(num)) return "R$ 0,00";
  return formatBRLFromDecimal(num);
}

/** Format a numeric decimal (NOT cents) as BRL — negatives keep `-` sign. */
export function formatBRLFromDecimal(value: number): string {
  if (!Number.isFinite(value)) return "R$ 0,00";
  if (value < 0) return `-R$ ${BRL_DIGITS.format(Math.abs(value))}`;
  return `R$ ${BRL_DIGITS.format(value)}`;
}

/** Cents → BRL with REGULAR space between `R$` and digits.
 *
 * Note: differs from {@link formatCurrencyCents}, which uses
 * `Intl.NumberFormat({style:'currency'})` and emits a non-breaking space
 * (U+00A0). Several legacy parity ports pin a regular space in their snapshot
 * tests (`screen.getByText("R$ 147,90")`) — use this helper there. */
export function formatBRLCents(cents: number | null | undefined): string {
  const value = typeof cents === "number" && Number.isFinite(cents) ? cents : 0;
  return `R$ ${BRL_DIGITS.format(value / 100)}`;
}

/**
 * Cents → BRL with NO decimals, rounded half-up. Compact format for header
 * surfaces where the centavos take vertical space without information value
 * (operator complaint: dashboard balance was too tall).
 *
 * Example: `formatBRLInteger(1000000)` → `"R$ 10.000"`.
 */
export function formatBRLInteger(cents: number | null | undefined): string {
  const value = typeof cents === "number" && Number.isFinite(cents) ? cents : 0;
  const reais = Math.round(value / 100);
  return `R$ ${reais.toLocaleString("pt-BR")}`;
}

// ── Dashboard parity helpers (R266: promoted from features/dashboard/_shared) ─
// Kept here so cross-feature surfaces (admin marketing, dashboard, robot) share
// a single source of truth. The dashboard module re-exports these for source
// compatibility with the parity-port commit history.

/** Decimal value as `R$ <value>` with regular space, falling back to R$ 0,00.
 *  Negative numbers render as `R$ -X,XX` (locale-natural minus). */
export function formatBRL(value: number): string {
  if (!Number.isFinite(value)) return "R$ 0,00";
  return `R$ ${BRL_DIGITS.format(value)}`;
}

/** Decimal value via Intl currency style (NBSP separator). Fallback R$ 0,00. */
export function formatBRLIntl(value: number): string {
  if (!Number.isFinite(value)) return "R$ 0,00";
  return BRL_FORMAT.format(value);
}

/** Format a numeric amount that arrives as a decimal string (e.g. "10.50")
 * using a runtime currency code. Falls back to `<currency> —` on bad input.
 * Treats the literal "R$" as BRL. Mirrors legacy ActiveSessionCard.formatAmount. */
export function formatAmountString(amount: string, currency = "BRL"): string {
  const num = Number(amount);
  const code = currency === "R$" ? "BRL" : currency.toUpperCase();
  if (!Number.isFinite(num)) return `${currency} —`;
  return num.toLocaleString("pt-BR", { style: "currency", currency: code });
}
